import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import React from 'react';

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Whether the sheet should close when clicking outside. Defaults to true. */
  closeOnClickOutside?: boolean;
  /** Whether to show the X close button in the top-right corner. Defaults to true. */
  showCloseButton?: boolean;
  /** Optional action button to display in the header */
  headerAction?: React.ReactNode;
}

export function ActionSheet({
  isOpen,
  onClose,
  title,
  description,
  icon,
  children,
  className,
  closeOnClickOutside = true,
  showCloseButton = true,
  headerAction,
}: ActionSheetProps) {
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        className={cn(
          // Mobile: full width (override default w-3/4)
          'w-full !max-w-none',
          // Tablet: 2/3 of screen width
          'md:!w-[66.666667vw] md:!max-w-[66.666667vw]',
          // PC: 1/2 of screen width
          'lg:!w-[50vw] lg:!max-w-[50vw]',
          // Max width to prevent excessive width on very large screens
          'xl:!max-w-4xl',
          // Enable scrolling for content overflow
          'overflow-y-auto',
          className
        )}
        showCloseButton={showCloseButton}
        onPointerDownOutside={e => {
          if (!closeOnClickOutside) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={e => {
          if (!closeOnClickOutside) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex flex-col items-start gap-2'>
              <SheetTitle
                className={cn('flex items-center gap-2', icon && 'gap-2')}
              >
                {icon}
                {title}
              </SheetTitle>
              {description && (
                <SheetDescription>{description}</SheetDescription>
              )}
            </div>
            {headerAction && (
              <div className='flex items-center mr-8'>{headerAction}</div>
            )}
          </div>
        </SheetHeader>

        <div className='p-4 pt-0'>{children}</div>
      </SheetContent>
    </Sheet>
  );
}
